import React, { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  DashboardOutlined,
  FileTextOutlined,
  HomeOutlined,
  TeamOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
} from '@ant-design/icons';

interface MenuItem {
  key: string;
  icon: React.ReactNode;
  label: string;
  path: string;
  roles?: ('admin' | 'editor')[];
}

const menuItems: MenuItem[] = [
  {
    key: 'dashboard',
    icon: <DashboardOutlined />,
    label: 'Dashboard',
    path: '/dashboard',
  },
  {
    key: 'sections',
    icon: <FileTextOutlined />,
    label: 'Sections',
    path: '/sections',
  },
  // {
  //   key: 'images',
  //   icon: <PictureOutlined />,
  //   label: 'Images',
  //   path: '/images',
  // },
  {
    key: 'homepage',
    icon: <HomeOutlined />,
    label: 'Homepage',
    path: '/homepage',
  },
  {
    key: 'users',
    icon: <TeamOutlined />,
    label: 'Users',
    path: '/users',
    roles: ['admin'],
  },
];

const AdminLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const filteredMenuItems = menuItems.filter(item => {
    if (!item.roles) return true;
    return item.roles.includes(user?.role || 'editor');
  });

  return (
    <div className="admin-layout">
      {/* Header */}
      <header className="admin-header">
        <div className="flex items-center">
          <button
            onClick={toggleCollapsed}
            className="flex items-center justify-center w-12 h-12 rounded-lg hover:bg-gray-1 transition-colors"
          >
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </button>
          <h1 className="ml-4 text-lg font-semibold text-gray-9">
            Coruscate creative studio Hub Admin
          </h1>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <UserOutlined className="text-gray-6" />
            <span className="text-sm text-gray-7">{user?.email}</span>
            <span className="px-2 py-1 text-xs rounded-full bg-primary-50 text-primary-600">
              {user?.role}
            </span>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-1 transition-colors"
          >
            <LogoutOutlined className="text-gray-6" />
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`admin-sider ${collapsed ? 'collapsed' : ''}`}>
          <nav className="p-4">
            <div className="space-y-1">
              {filteredMenuItems.map((item) => {
                const isActive = location.pathname.startsWith(item.path);
                return (
                  <Link
                    key={item.key}
                    to={item.path}
                    className={`admin-nav-item flex items-center space-x-3 text-sm font-medium rounded-lg transition-colors ${
                      isActive ? 'active' : ''
                    }`}
                  >
                    <span className="flex-shrink-0 text-base">{item.icon}</span>
                    {!collapsed && <span>{item.label}</span>}
                  </Link>
                );
              })}
            </div>
            
            {!collapsed && (
              <div className="mt-8 pt-4 border-t border-gray-3">
                <div className="text-xs text-gray-5 uppercase tracking-wide mb-2">
                  System
                </div>
                <Link
                  to="/settings"
                  className="admin-nav-item flex items-center space-x-3 text-sm font-medium rounded-lg transition-colors"
                >
                  <SettingOutlined className="flex-shrink-0 text-base" />
                  <span>Settings</span>
                </Link>
              </div>
            )}
          </nav>
        </aside>

        {/* Main Content */}
        <main className={`admin-content flex-1 ${collapsed ? 'ml-20' : 'ml-50'}`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;